import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXsrfConfiguration
} from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { setUser } from './STORE/actions/auth.actions';
import { HttpBaseService } from './base/http-base.service';
import { ApiConstants } from './base/api-constants';
import { BrowserModule, Title } from '@angular/platform-browser';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { BadgeModule } from 'primeng/badge';
import { ChipsModule } from 'primeng/chips';
import { SelectModule } from 'primeng/select';
import { effects } from './STORE/effects/effects';
import { reducers } from './STORE/reducers/reducers';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ApiInterceptor } from './base/api-interceptor';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { BranchDropdownHeaderComponent } from './pages/settings/branch-management/branch-dropdown-header/branch-dropdown-header.component';
import { ProductLogoComponent } from '@svg-icons/product-logo/product-logo.component';
import { InboxIconComponent } from '@svg-icons/inbox-icon/inbox-icon.component';
import { CompassIconComponent } from '@svg-icons/compass-icon/compass-icon.component';
import { MessageIconComponent } from '@svg-icons/message-icon/message-icon.component';
import { ToggleViewIconComponent } from '@svg-icons/toggle-view-icon/toggle-view-icon.component';
import { CubeIconComponent } from '@svg-icons/cube-icon/cube-icon.component';
import { PreviewEyeIconComponent } from '@svg-icons/preview-eye-icon/preview-eye-icon.component';
import { CodeIconComponent } from '@svg-icons/code-icon/code-icon.component';
import { PublishAppComponent } from '@svg-icons/publish-app/publish-app.component';
import { SearchIconComponent } from '@svg-icons/search-icon/search-icon.component';
import { PlayIconComponent } from '@svg-icons/play-icon/play-icon.component';
import { RtIconCloudComponent } from '@rt-icons/rt-icon-cloud/rt-icon-cloud.component';
import { RtIconBillingHomeComponent } from '@rt-icons/rt-icon-billing-home/rt-icon-billing-home.component';
import { ClipboardCheckIconComponent } from '@svg-icons/clipboard-check-icon/clipboard-check-icon.component';
import { RocketIconComponent } from '@svg-icons/rocket-icon/rocket-icon.component';
import { RtIconSettings01Component } from '@rt-icons/rt-icon-settings-01/rt-icon-settings-01.component';
import { SharedModule } from './components/shared.module';
import { SideBarComponent } from './components/side-bar/side-bar.component';
import { HeaderSidebarComponent } from './components/header-sidebar/header-sidebar.component';
import { ThemeSettingsComponent } from './components/theme-settings/theme-settings.component';
import { CustomDirectivesModule } from './directives/custom-directives.module';

import { HomeComponent } from './pages/home/home.component';

import { DatePipe, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { localStorageSyncReducer } from './STORE/reducers/localstorage.reducer';

import { NgSelectModule } from '@ng-select/ng-select';
import { MonacoEditorModule, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { AvatarModule } from 'primeng/avatar';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputMaskModule } from 'primeng/inputmask';
import { KeyFilterModule } from 'primeng/keyfilter';
import { MessagesModule } from 'primeng/messages';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { CanDeactivateGuard } from './auth/deactivate.guard';
import { GlobalErrorHandler } from './base/GlobalErrorHandler';
import { AppLoaderComponent } from './components/app-loader/app-loader.component';
import { getPendingService } from './services/pendingHttpServices.service';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ProgressBarModule } from 'primeng/progressbar';
import { FirebaseAppModule } from '@angular/fire/app';
import { FirebaseModule } from 'src/firebase/firebase.module';
import { ButtonModule } from 'primeng/button';

import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Lara from '@primeng/themes/lara';
import { definePreset } from '@primeng/themes';
import { DialogService } from 'primeng/dynamicdialog';
import { PopoverModule } from 'primeng/popover';

import { RtIconGrid03Component } from '@rt-icons/rt-icon-grid-03/rt-icon-grid-03.component';
import { RtIconBriefcase02Component } from '@rt-icons/rt-icon-briefcase-02/rt-icon-briefcase-02.component';
import { RtIconZapFastComponent } from '@rt-icons/rt-icon-zap-fast/rt-icon-zap-fast.component';
import { RtIconFileCode01Component } from '@rt-icons/rt-icon-file-code-01/rt-icon-file-code-01.component';
import { RappitTitleService } from './services/rappit-title.service';
import { ServiceWorkerModule } from '@angular/service-worker';

export function appInitializerFactory(translate: TranslateService) {
  return () => {
    translate.setDefaultLang('en');
    return translate.use('en').toPromise();
  };
}

/**
 * Factory function to fetch fresh auth data during app bootstrap.
 * This ensures the NgRx store has current user data before any guards run,
 * preventing stale localStorage data from being used for role-based access.
 */
export function authInitializerFactory(injector: Injector) {
  return () => {
    const httpBaseService = injector.get(HttpBaseService);
    const store = injector.get(Store);

    return firstValueFrom(
      httpBaseService.get({ ...ApiConstants.authenticateUser, showErrorMsg: false }).pipe(
        tap((userData: any) => {
          store.dispatch(setUser({ userData }));
        }),
        catchError((error) => {
          console.warn('Auth initialization failed, user will be redirected to login:', error?.status);
          // Return empty observable to allow app to continue loading
          // AuthGuard will handle the redirect to login
          return of(null);
        })
      )
    );
  };
}

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const isIframe = window.location !== window.parent.location;
const metaReducers: MetaReducer<any, any>[] = !isIframe ? [localStorageSyncReducer] : [];
const monacoConfig: NgxMonacoEditorConfig = {
  onMonacoLoad: () => {
    (window as any).monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: (window as any).monaco.languages.typescript.ScriptTarget.ES2016,
      allowNonTsExtensions: true,
      moduleResolution: (window as any).monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: (window as any).monaco.languages.typescript.ModuleKind.CommonJS,
      noEmit: true,
      noLib: true,
      typeRoots: ['node_modules/@types'],
      experimentalDecorators: true,
      automaticLayout: true
    });
  } // here monaco object will be available as window.monaco use this function to extend monaco editor functionalities.
};
@NgModule({
  declarations: [
    AppComponent,
    SideBarComponent,
    HeaderComponent,
    HeaderSidebarComponent,
    FooterComponent,
    ThemeSettingsComponent,
    HomeComponent,
    AppLoaderComponent
  ],
  bootstrap: [AppComponent],
  exports: [MonacoEditorModule],
  imports: [
    PopoverModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    SelectModule,
    DialogModule,
    ButtonModule,
    SplitButtonModule,
    ProgressBarModule,
    ChipsModule,
    AutoCompleteModule,
    TableModule,
    CustomDirectivesModule,
    FirebaseModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(effects),
    BadgeModule,
    ProductLogoComponent,
    InboxIconComponent,
    CompassIconComponent,
    MessageIconComponent,
    ToggleViewIconComponent,
    CubeIconComponent,
    PreviewEyeIconComponent,
    CodeIconComponent,
    PublishAppComponent,
    SearchIconComponent,
    PlayIconComponent,
    RtIconCloudComponent,
    RtIconBillingHomeComponent,
    ClipboardCheckIconComponent,
    RocketIconComponent,
    RtIconSettings01Component,
    SharedModule,
    BranchDropdownHeaderComponent,
    AvatarModule,
    ConfirmDialogModule,
    MessagesModule,
    TooltipModule,
    ToastModule,
    DialogModule,
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    AngularSvgIconModule.forRoot(),
    InputMaskModule,
    KeyFilterModule,
    MenuModule,
    OverlayPanelModule,
    MonacoEditorModule.forRoot(monacoConfig),
    NgSelectModule,

    RtIconGrid03Component,
    RtIconBriefcase02Component,
    RtIconZapFastComponent,
    RtIconFileCode01Component,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: true,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: authInitializerFactory,
      deps: [Injector],
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: Title, useClass: RappitTitleService },
    getPendingService,
    ConfirmationService,
    MessageService,
    DatePipe,
    CanDeactivateGuard,
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({
        cookieName: 'XSRF-TOKEN',
        headerName: 'XSRF-TOKEN'
      })
    ),

    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: definePreset(Lara, {
          semantic: {
            primary: {
              50: '#e3f2fd',
              100: '#bbdefb',
              200: '#90caf9',
              300: '#64b5f6',
              400: '#42a5f5',
              500: '#1e88e5',
              600: '#1976d2',
              700: '#1565c0',
              800: '#0d47a1',
              900: '#0d47a1',
              950: '#082963'
            },
            success: {
              50: '#e8f5e9',
              100: '#c8e6c9',
              200: '#a5d6a7',
              300: '#81c784',
              400: '#66bb6a',
              500: '#4caf50',
              600: '#43a047',
              700: '#388e3c',
              800: '#2e7d32',
              900: '#1b5e20',
              950: '#0c3613'
            },
            info: {
              50: '#e1f5fe',
              100: '#b3e5fc',
              200: '#81d4fa',
              300: '#4fc3f7',
              400: '#29b6f6',
              500: '#03a9f4',
              600: '#039be5',
              700: '#0288d1',
              800: '#01579b',
              900: '#01579b',
              950: '#002240'
            },
            warning: {
              50: '#fffde7',
              100: '#fff9c4',
              200: '#fff59d',
              300: '#fff176',
              400: '#ffee58',
              500: '#ffeb3b',
              600: '#fdd835',
              700: '#fbc02d',
              800: '#f9a825',
              900: '#f57f17',
              950: '#914f0e'
            },
            danger: {
              50: '#ffebee',
              100: '#ffcdd2',
              200: '#ef9a9a',
              300: '#e57373',
              400: '#ef5350',
              500: '#f44336',
              600: '#e53935',
              700: '#d32f2f',
              800: '#c62828',
              900: '#b71c1c',
              950: '#620e0e'
            }
          }
        }),
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: 'primeng, custom'
          }
        }
      }
    }),
    DialogService
  ]
})
export class AppModule {}


